import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Radio, Video, XCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function AdminLive() {
    const [settings, setSettings] = useState({});
    const [plans, setPlans] = useState(null);
    const [active, setActive] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [s, p, a] = await Promise.all([
            api.get('/admin/settings'),
            api.get('/admin/live/plans'),
            api.get('/admin/livestreams/active'),
        ]);
        setSettings(s.data || {}); setPlans(p.data); setActive(a.data || []);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put('/admin/settings', {
                agora_app_id: settings.agora_app_id || null,
                agora_app_certificate: settings.agora_app_certificate || null,
                agora_region: settings.agora_region || null,
                live_commission_extra_pct: settings.live_commission_extra_pct !== '' && settings.live_commission_extra_pct !== undefined ? parseFloat(settings.live_commission_extra_pct) : null,
            });
            toast.success('Configuración guardada'); load();
        } catch (e) { toast.error('Error'); }
        finally { setBusy(false); }
    };

    const kill = async (id) => {
        if (!window.confirm('¿Forzar el cierre de este live?')) return;
        await api.post(`/admin/livestreams/${id}/kill`); toast.success('Live cerrado'); load();
    };

    return (
        <div className="space-y-4" data-testid="admin-live-page">
            <div className="flex items-center gap-2"><Radio className="h-6 w-6 text-red-600" /><h1 className="text-2xl font-semibold">OkLive Shop / Agora</h1></div>

            {plans && (
                <div className="flex items-center gap-2 text-sm">
                    Estado: <Badge className={plans.agora_configured ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>{plans.agora_configured ? 'Configurado' : 'Modo simulación'}</Badge>
                    <Badge className="bg-slate-100 text-slate-700">Comisión extra live: +{plans.commission_extra_pct}%</Badge>
                </div>
            )}

            <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" />Credenciales Agora</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label>Agora App ID</Label><Input value={settings.agora_app_id || ''} onChange={e => setSettings({ ...settings, agora_app_id: e.target.value })} placeholder="Deja vacío para no cambiar" data-testid="agora-app-id" /></div>
                    <div><Label>Agora App Certificate</Label><Input type="password" value={settings.agora_app_certificate || ''} onChange={e => setSettings({ ...settings, agora_app_certificate: e.target.value })} placeholder="Deja vacío para no cambiar" data-testid="agora-cert" /></div>
                    <div><Label>Región (opcional)</Label><Input value={settings.agora_region || ''} onChange={e => setSettings({ ...settings, agora_region: e.target.value })} placeholder="us, la, eu" /></div>
                    <div><Label>Comisión extra en ventas live (%)</Label><Input type="number" step="0.1" value={settings.live_commission_extra_pct ?? ''} onChange={e => setSettings({ ...settings, live_commission_extra_pct: e.target.value })} placeholder="1.0" data-testid="live-extra-commission" /></div>
                    <div className="md:col-span-2 flex justify-end"><Button onClick={save} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="admin-live-save">Guardar</Button></div>
                </CardContent>
            </Card>

            {plans && (
                <Card>
                    <CardHeader><CardTitle>Planes por tier Pro</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {Object.entries(plans.plans).map(([code, p]) => (
                            <div key={code} className="flex items-center gap-3 border rounded p-3">
                                <Badge className="uppercase">{code}</Badge>
                                <div className="text-sm">{p.live_minutes_month} min/mes • {p.max_quality} • {p.max_viewers} viewers • {p.recording ? 'con grabación' : 'sin grabación'}</div>
                            </div>
                        ))}
                        <div className="text-xs text-muted-foreground">Overage: ${plans.overage_per_min}/min-viewer. Los planes son configurables en el futuro desde código o backend.</div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Video className="h-5 w-5" />Lives activos ({active.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    {active.length === 0 && <div className="text-sm text-muted-foreground">Ningún live activo ahora.</div>}
                    {active.map(l => (
                        <div key={l.id} className="flex items-center justify-between border rounded p-3" data-testid={`admin-live-${l.id}`}>
                            <div>
                                <div className="font-medium">{l.title}</div>
                                <div className="text-xs text-muted-foreground">@{l.seller_nickname} • {l.current_viewers || 0} viewers • {l.sales_count || 0} ventas</div>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => kill(l.id)} className="border-red-600 text-red-600 hover:bg-red-50" data-testid={`admin-kill-${l.id}`}><XCircle className="h-4 w-4 mr-1" />Cerrar</Button>
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
}
